import React, { useState } from 'react';
import { notify, confirmDialog } from '../ui/notify';
import { apiCall, authHeaders } from '../config';

const SignageManager = () => {
  const [signs, setSigns] = useState([]);
  const [activeSignType, setActiveSignType] = useState('numbering');

  // Nummerierungsschild State
  const [numberingForm, setNumberingForm] = useState({
    fromNumber: '',
    toNumber: '',
    arrowDirection: 'right'
  });

  // Auto-Nummerierung State
  const [autoNumberingForm, setAutoNumberingForm] = useState({
    startNumber: '',
    endNumber: ''
  });

  // Haken-Etiketten: selbstklebende Labels TopStick 8780 (25,4 × 10 mm, 189 je A4-Bogen)
  const [labelForm, setLabelForm] = useState({
    from: '', to: '', prefix: '', startPos: 1, perNumber: 1, offX: 0, offY: 0, frames: false
  });

  const signTypes = {
    numbering: { name: 'Nummerierung', icon: '🔢', editable: true },
    auto_numbering: { name: 'Auto-Nummerierung (10er-Blöcke)', icon: '🔄', editable: true },
    no_bike_parking: { name: 'Fahrrad abstellen verboten', icon: '🚲🚫', editable: false },
    closed_emergency: { name: 'Geschlossen - Notdienst', icon: '🚨', editable: false },
    closed_baggage: { name: 'Geschlossen - Gepäck', icon: '🧳', editable: false },
    pram_warning: { name: 'Kinderwagen Hinweis', icon: '👶☔', editable: false },
    fire_hydrant: { name: 'Feuerwehr H-Schild', icon: '🚒', editable: false },
    price_5euro: { name: 'Preis 5€', icon: '💰', editable: false },
    baggage_dropoff: { name: 'Gepäckannahme', icon: '📥', editable: false },
    baggage_pickup: { name: 'Gepäckabholung', icon: '📤', editable: false },
    closing_time: { name: 'Schließzeit 00:30 Uhr', icon: '⏰', editable: false },
    liability_limit: { name: 'Haftungsobergrenze 100€', icon: '⚖️', editable: false },
    hook_labels: { name: 'Haken-Etiketten (TopStick 8780, 25,4 × 10 mm)', icon: '🏷️', editable: true }
  };

  const arrowSymbols = {
    right: '→', left: '←', up: '↑', down: '↓'
  };

  const addSign = () => {
    if (activeSignType === 'auto_numbering') {
      // Auto-Nummerierung: Erstelle mehrere Schilder in 10er-Blöcken
      const startNum = parseInt(autoNumberingForm.startNumber);
      const endNum = parseInt(autoNumberingForm.endNumber);
      
      if (isNaN(startNum) || isNaN(endNum) || startNum >= endNum) {
        notify('Bitte geben Sie gültige Start- und Endnummern ein.');
        return;
      }

      const newSigns = [];
      for (let current = startNum; current <= endNum; current += 10) {
        const blockEnd = Math.min(current + 9, endNum);
        const newSign = {
          id: Date.now() + current, // Eindeutige ID
          type: 'numbering', // Verwende numbering type für die Darstellung
          fromNumber: current.toString(),
          toNumber: blockEnd.toString(),
          arrowDirection: 'down',
          title: `${current} - ${blockEnd}`,
          content: arrowSymbols['down']
        };
        newSigns.push(newSign);
      }
      
      setSigns([...signs, ...newSigns]);
      resetForm();
    } else {
      // Normale Schilder
      const newSign = {
        id: Date.now(),
        type: activeSignType,
        ...getSignContent(activeSignType)
      };
      setSigns([...signs, newSign]);
      resetForm();
    }
  };

  const getSignContent = (type) => {
    switch (type) {
      case 'numbering':
        return {
          fromNumber: numberingForm.fromNumber,
          toNumber: numberingForm.toNumber,
          arrowDirection: numberingForm.arrowDirection,
          title: numberingForm.fromNumber + ' - ' + numberingForm.toNumber,
          content: arrowSymbols[numberingForm.arrowDirection]
        };
      case 'no_bike_parking':
        return {
          title: 'FAHRRAD ABSTELLEN VERBOTEN',
          content: 'Das Abstellen von Fahrrädern ist hier nicht gestattet.\nFahrräder werden kostenpflichtig entfernt.',
          icon: '🚲🚫'
        };
      case 'closed_emergency':
        return {
          title: 'WIR HABEN GESCHLOSSEN',
          content: 'Sie können unseren Notdienst erreichen:\n📞 017643831566\n\nWE ARE CLOSED\nYou can reach our emergency service:\n📞 017643831566',
          icon: '🚨'
        };
      case 'closed_baggage':
        return {
          title: 'WIR HABEN GESCHLOSSEN',
          content: 'Ihr Gepäckstück befindet sich nicht mehr auf der Station.\n\nWir sind morgen ab 9 Uhr wieder für Sie geöffnet.',
          icon: '🧳'
        };
      case 'pram_warning':
        return {
          title: 'ACHTUNG: KINDERWAGEN IM FREIEN',
          content: 'Kinderwagen werden im Außenbereich abgestellt.\n\nBitte bringen Sie Abdeckungen mit.\n\nKeine Haftung für Schäden.',
          icon: '👶☔'
        };
      case 'fire_hydrant':
        return { title: '', content: '', icon: 'H' };
      case 'price_5euro':
        return { title: 'Alle Gepäckstücke / Kinderwagen', content: '5€\npro Tag', icon: '💰' };
      case 'baggage_dropoff':
        return {
          title: '↓ DROP-OFF ↓\n\nGEPÄCKANNAHME',
          content: 'Bitte geben Sie Ihr Gepäck hier ab.',
          icon: ''
        };
      case 'baggage_pickup':
        return {
          title: '↓ PICK-UP ↓\n\nGEPÄCKABHOLUNG',
          content: 'Bitte holen Sie Ihr Gepäck hier ab.',
          icon: ''
        };
      case 'closing_time':
        return {
          title: 'WE CLOSE AT 00:30 UHR',
          content: 'Bitte holen Sie Ihr Gepäck rechtzeitig ab.\n\nVielen Dank für Ihr Verständnis.',
          icon: '⏰'
        };
      case 'liability_limit':
        return {
          title: '',
          content: 'Haftungsobergrenze\n100€',
          icon: '⚖️'
        };
      default:
        return { title: '', content: '' };
    }
  };

  // Bogen-Geometrie TopStick 8780 – NICHT die Katalogmaße, sondern 1:1 aus Philipps
  // Excel-Vorlage „Etiketten 25,4x10.xlsx" ausgelesen, die am Drucker nachweislich
  // exakt sitzt: Rand links 8,0 mm, oben 13,0 mm, Spaltenraster 27,78 mm (Etikett
  // 25,4 + Steg), Zeilenhöhen im eingemessenen Wechsel 28,5 / 27,75 pt. Die Nummer
  // sitzt wie in Excel mittig im Raster. Gedruckt wird direkt mit jsPDF in
  // Millimetern – der Server-PDF-Generator erzwingt Querformat und skaliert.
  const PT = 25.4 / 72;
  const EXCEL_ROWS_PT = [28.5, 28.5, 28.5, 27.75, 27.75, 28.5, 28.5, 27.75, 27.75, 27.75, 27.75, 28.5, 28.5,
    27.75, 27.75, 27.75, 27.75, 28.5, 27.75, 27.75, 27.75, 27.75, 27.75, 28.5, 28.5, 27.75, 28.5];
  const LABEL = {
    cols: 7, rows: 27, w: 25.4, h: 10,
    left: 22.677 * PT,          // 8,0 mm
    top: 36.85 * PT,            // 13,0 mm
    pitchX: 78.75 * PT,         // 27,78 mm je Spalte
    rowTop: EXCEL_ROWS_PT.map((_, i) => EXCEL_ROWS_PT.slice(0, i).reduce((a, b) => a + b, 0) * PT),
    rowH: EXCEL_ROWS_PT.map((h) => h * PT),
    fontPt: 26,                 // Calibri 26 fett in der Vorlage
  };
  const labelStats = () => {
    const from = parseInt(labelForm.from, 10), to = parseInt(labelForm.to, 10);
    if (isNaN(from) || isNaN(to) || to < from) return null;
    const perPage = LABEL.cols * LABEL.rows;
    const count = (to - from + 1) * (Number(labelForm.perNumber) === 2 ? 2 : 1);
    const startPos = Math.min(perPage, Math.max(1, parseInt(labelForm.startPos, 10) || 1));
    const pages = Math.ceil((startPos - 1 + count) / perPage);
    const restLast = pages * perPage - (startPos - 1 + count);
    return { count, pages, perPage, restLast };
  };

  const generateLabelSheet = async () => {
    const stats = labelStats();
    if (!stats) { notify('Bitte gültige Von/Bis-Nummern eingeben.'); return; }
    if (stats.count > 5000) { notify('Das sind über 5000 Etiketten – bitte in kleineren Bereichen drucken.'); return; }
    const from = parseInt(labelForm.from, 10), to = parseInt(labelForm.to, 10);
    const per = Number(labelForm.perNumber) === 2 ? 2 : 1;
    const items = [];
    for (let n = from; n <= to; n++) for (let k = 0; k < per; k++) items.push(`${labelForm.prefix || ''}${n}`);

    const { default: jsPDF } = await import('jspdf');
    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'portrait' });
    pdf.setFont('helvetica', 'bold');
    const offX = parseFloat(labelForm.offX) || 0, offY = parseFloat(labelForm.offY) || 0;
    const perPage = LABEL.cols * LABEL.rows;
    let slot = Math.min(perPage, Math.max(1, parseInt(labelForm.startPos, 10) || 1)) - 1;
    items.forEach((text, i) => {
      if (i > 0 && slot % perPage === 0) pdf.addPage();
      const idx = slot % perPage;
      const col = idx % LABEL.cols, row = Math.floor(idx / LABEL.cols);
      // Rasterzelle wie in Excel; das Etikett sitzt mittig im Raster
      const cx = LABEL.left + offX + col * LABEL.pitchX + LABEL.pitchX / 2;
      const cy = LABEL.top + offY + LABEL.rowTop[row] + LABEL.rowH[row] / 2;
      if (labelForm.frames) {
        pdf.setDrawColor(190); pdf.setLineWidth(0.1);
        pdf.rect(cx - LABEL.w / 2, cy - LABEL.h / 2, LABEL.w, LABEL.h);
      }
      // Schriftgröße nach Länge: 4 Ziffern wie in der Vorlage (26 pt), längere Texte schrumpfen
      pdf.setFontSize(text.length <= 4 ? LABEL.fontPt : text.length <= 6 ? 19 : 14);
      pdf.text(text, cx, cy, { align: 'center', baseline: 'middle' });
      slot++;
    });
    pdf.save(`haken-etiketten_${labelForm.prefix || ''}${from}-${to}.pdf`);
  };

  const resetForm = () => {
    setNumberingForm({ fromNumber: '', toNumber: '', arrowDirection: 'right' });
    setAutoNumberingForm({ startNumber: '', endNumber: '' });
  };

  const removeSign = (id) => {
    setSigns(signs.filter(sign => sign.id !== id));
  };

  const generatePDF = async () => {
    if (signs.length === 0) {
      notify('Bitte fügen Sie mindestens ein Schild hinzu.');
      return;
    }

    try {
      console.log('🎯 Generating PDF for', signs.length, 'signs');
      
      const signagesHTML = signs.map(sign => {
        const signContent = getSignContent(sign.type);
        
        if (sign.type === 'numbering') {
          return '<div class="sign-page"><div class="numbering-sign"><div class="number-text">' + sign.title + '</div><div class="arrow-text">' + sign.content + '</div></div></div>';
        } else if (sign.type === 'fire_hydrant') {
          return '<div class="sign-page"><div class="fire-hydrant-sign"><div class="hydrant-h">H</div></div></div>';
        } else if (sign.type === 'price_5euro') {
          return '<div class="sign-page"><div class="price-sign"><div class="price-title">' + (sign.title || signContent.title) + '</div><div class="price-amount">5€</div><div class="price-detail">pro Tag</div></div></div>';
        } else if (sign.type === 'liability_limit') {
          return '<div class="sign-page"><div class="liability-sign"><div class="liability-text">' + (sign.content || signContent.content) + '</div></div></div>';
        } else if (sign.type === 'no_bike_parking') {
          const content = sign.content || signContent.content;
          const [title, subtitle] = content.split('\n');
          return '<div class="sign-page"><div class="standard-sign"><div class="bike-forbidden-icon"><div class="bike-and-cross"><div class="bike-symbol">🚲</div><div class="cross-symbol">✕</div></div></div><div class="sign-title">' + (sign.title || signContent.title) + '</div><div class="sign-content">' + subtitle + '</div></div></div>';
        } else if (sign.type === 'baggage_dropoff') {
          return '<div class="sign-page"><div class="baggage-pickup-sign"><div class="pickup-english">↓ DROP-OFF ↓</div><div class="pickup-spacer"></div><div class="pickup-german">GEPÄCKANNAHME</div><div class="baggage-content">' + (sign.content || signContent.content) + '</div></div></div>';
        } else if (sign.type === 'baggage_pickup') {
          return '<div class="sign-page"><div class="baggage-pickup-sign"><div class="pickup-english">↓ PICK-UP ↓</div><div class="pickup-spacer"></div><div class="pickup-german">GEPÄCKABHOLUNG</div><div class="baggage-content">' + (sign.content || signContent.content) + '</div></div></div>';
        } else if (sign.type === 'closing_time') {
          return '<div class="sign-page"><div class="closing-time-sign"><div class="closing-icon">' + (sign.icon || signContent.icon) + '</div><div class="closing-title">' + (sign.title || signContent.title) + '</div><div class="closing-content">' + (sign.content || signContent.content) + '</div></div></div>';
        } else {
          return '<div class="sign-page"><div class="standard-sign"><div class="sign-icon">' + (sign.icon || signContent.icon) + '</div><div class="sign-title">' + (sign.title || signContent.title) + '</div><div class="sign-content">' + (sign.content || signContent.content) + '</div></div></div>';
        }
      }).join('');

      const htmlHeader = '<!DOCTYPE html><html lang="de"><head><meta charset="UTF-8"><title>Beschilderung</title><style>';
      const cssStyles = '* { margin: 0; padding: 0; box-sizing: border-box; }body { font-family: Arial, sans-serif; background: white;}.sign-page {width: 297mm;height: 210mm;page-break-after: always;display: flex;align-items: center;justify-content: center;background: white;position: relative;}.sign-page:last-child {page-break-after: avoid;}.numbering-sign {width: 100%;height: 100%;display: flex;flex-direction: column;align-items: center;justify-content: center;padding: 20mm;text-align: center;}.number-text {font-size: 100pt;font-weight: bold;color: #000000;line-height: 1;margin-bottom: 15mm;}.arrow-text {font-size: 200pt;color: #000000;line-height: 0.8;margin-top: 0;}.standard-sign {text-align: center;max-width: 280mm;padding: 15mm;display: flex;flex-direction: column;justify-content: center;height: 100%;}.sign-icon {font-size: 80pt;margin-bottom: 10mm;line-height: 1;}.sign-title {font-size: 28pt;font-weight: bold;margin-bottom: 8mm;color: #333;line-height: 1.1;}.sign-content {font-size: 16pt;color: #666;white-space: pre-line;line-height: 1.2;max-width: 260mm;margin: 0 auto;}.baggage-sign {text-align: center;max-width: 280mm;padding: 15mm;display: flex;flex-direction: column;justify-content: center;height: 100%;}.baggage-icon {font-size: 240pt;margin-bottom: 15mm;line-height: 1;}.baggage-title {font-size: 84pt;font-weight: bold;margin-bottom: 15mm;color: #333;line-height: 0.9;}.baggage-content {font-size: 48pt;color: #666;white-space: pre-line;line-height: 1.1;max-width: 260mm;margin: 0 auto;}.baggage-pickup-sign {text-align: center;max-width: 280mm;padding: 15mm;display: flex;flex-direction: column;justify-content: center;height: 100%;}.pickup-english {font-size: 84pt;font-weight: bold;color: #333;line-height: 1;margin-bottom: 10mm;}.pickup-spacer {height: 20mm;}.pickup-german {font-size: 84pt;font-weight: bold;color: #333;line-height: 1;margin-bottom: 15mm;}.closing-time-sign {text-align: center;max-width: 280mm;padding: 15mm;display: flex;flex-direction: column;justify-content: center;height: 100%;}.closing-icon {font-size: 216pt;margin-bottom: 13mm;line-height: 1;}.closing-title {font-size: 76pt;font-weight: bold;margin-bottom: 13mm;color: #333;line-height: 0.8;}.closing-content {font-size: 43pt;color: #666;white-space: pre-line;line-height: 1.0;max-width: 260mm;margin: 0 auto;}.fire-hydrant-sign {width: 100%;height: 100%;display: flex;align-items: center;justify-content: center;text-align: center;}.hydrant-h {font-size: 600pt;font-weight: bold;color: #000000;line-height: 1;font-family: Arial Black, Arial, sans-serif;}.price-sign {width: 100%;height: 100%;display: flex;flex-direction: column;align-items: center;justify-content: center;text-align: center;padding: 20mm;}.price-title {font-size: 28pt;font-weight: bold;color: #333;line-height: 1.1;margin-bottom: 15mm;}.price-amount {font-size: 240pt;font-weight: bold;color: #000000;line-height: 1;margin-bottom: 10mm;}.price-detail {font-size: 48pt;color: #666;line-height: 1;}.liability-sign {width: 100%;height: 100%;display: flex;align-items: center;justify-content: center;text-align: center;padding: 20mm;}.liability-text {font-size: 85pt;font-weight: bold;color: #000000;line-height: 1;white-space: pre-line;}.bike-forbidden-icon {position: relative;display: inline-block;margin-bottom: 10mm;}.bike-and-cross {position: relative;display: inline-block;}.bike-symbol {font-size: 150pt;color: #000;position: relative;z-index: 1;display: inline-block;}.cross-symbol {font-size: 80pt;color: #e74c3c;position: absolute;top: 50%;left: 50%;transform: translate(-50%, -50%);z-index: 2;text-shadow: 2px 2px 4px rgba(255,255,255,0.8);}@media print {.sign-page {margin: 0;width: 297mm;height: 210mm;page-break-inside: avoid;}}';
      const htmlFooter = '</style></head><body>' + signagesHTML + '</body></html>';
      const fullHTML = htmlHeader + cssStyles + htmlFooter;

      // For PDF generation, we need to use fetch directly to get the blob response
      let baseUrl;
      if (process.env.NODE_ENV === 'production') {
        baseUrl = '/api';
      } else {
        baseUrl = 'http://localhost:4000/api';
      }
      
      const response = await fetch(`${baseUrl}/generate-signage-pdf`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', ...authHeaders() },
        body: JSON.stringify({ html: fullHTML, filename: 'beschilderung_' + new Date().toISOString().split('T')[0] + '.pdf' }),
      });

      if (response.ok) {
        const blob = await response.blob();
        const url = window.URL.createObjectURL(blob);
        const a = document.createElement('a');
        a.style.display = 'none';
        a.href = url;
        a.download = 'beschilderung_' + new Date().toISOString().split('T')[0] + '.pdf';
        document.body.appendChild(a);
        a.click();
        window.URL.revokeObjectURL(url);
        console.log('✅ PDF generated successfully!');
      } else {
        throw new Error('PDF generation failed');
      }
    } catch (error) {
      console.error('❌ PDF generation error:', error);
      notify('Fehler bei der PDF-Generierung: ' + error.message);
    }
  };

  return (
    <div className="row">
      <div className="col-md-4">
        <div className="card">
          <div className="card-header">
            <h5>🏷️ Beschilderung erstellen</h5>
          </div>
          <div className="card-body">
            <div className="mb-3">
              <label className="form-label">Schildtyp</label>
              <select className="form-select" value={activeSignType} onChange={(e) => setActiveSignType(e.target.value)}>
                {Object.entries(signTypes).map(([key, type]) => (
                  <option key={key} value={key}>{type.icon} {type.name}</option>
                ))}
              </select>
            </div>

            {activeSignType === 'numbering' && (
              <>
                <div className="mb-3">
                  <label className="form-label">Von Nummer</label>
                  <input type="number" className="form-control" value={numberingForm.fromNumber} onChange={(e) => setNumberingForm({...numberingForm, fromNumber: e.target.value})} placeholder="z.B. 1" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Bis Nummer</label>
                  <input type="number" className="form-control" value={numberingForm.toNumber} onChange={(e) => setNumberingForm({...numberingForm, toNumber: e.target.value})} placeholder="z.B. 50" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Pfeilrichtung</label>
                  <select className="form-select" value={numberingForm.arrowDirection} onChange={(e) => setNumberingForm({...numberingForm, arrowDirection: e.target.value})}>
                    <option value="right">→ Rechts</option>
                    <option value="left">← Links</option>
                    <option value="up">↑ Oben</option>
                    <option value="down">↓ Unten</option>
                  </select>
                </div>
              </>
            )}

            {activeSignType === 'auto_numbering' && (
              <>
                <div className="mb-3">
                  <label className="form-label">Startnummer</label>
                  <input type="number" className="form-control" value={autoNumberingForm.startNumber} onChange={(e) => setAutoNumberingForm({...autoNumberingForm, startNumber: e.target.value})} placeholder="z.B. 6000" />
                </div>
                <div className="mb-3">
                  <label className="form-label">Endnummer</label>
                  <input type="number" className="form-control" value={autoNumberingForm.endNumber} onChange={(e) => setAutoNumberingForm({...autoNumberingForm, endNumber: e.target.value})} placeholder="z.B. 6099" />
                </div>
                <div className="alert alert-info">
                  <strong>ℹ️ Automatische 10er-Blöcke</strong><br/>
                  Es werden automatisch Schilder in 10er-Schritten erstellt (z.B. 6000-6009, 6010-6019, etc.).<br/>
                  Alle Pfeile zeigen nach unten.
                </div>
              </>
            )}

            {activeSignType === 'hook_labels' && (
              <>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label">Von Nummer</label>
                    <input type="number" className="form-control" value={labelForm.from}
                      onChange={(e) => setLabelForm({ ...labelForm, from: e.target.value })} placeholder="z.B. 1" />
                  </div>
                  <div className="col-6">
                    <label className="form-label">Bis Nummer</label>
                    <input type="number" className="form-control" value={labelForm.to}
                      onChange={(e) => setLabelForm({ ...labelForm, to: e.target.value })} placeholder="z.B. 378" />
                  </div>
                </div>
                <div className="row g-2 mb-3">
                  <div className="col-6">
                    <label className="form-label">Präfix (optional)</label>
                    <input type="text" className="form-control" value={labelForm.prefix} maxLength={4}
                      onChange={(e) => setLabelForm({ ...labelForm, prefix: e.target.value })} placeholder="z.B. G1-" />
                  </div>
                  <div className="col-6">
                    <label className="form-label" title="Bei einem angebrochenen Bogen: ab welchem Etikett (1–189, zeilenweise) gedruckt wird">
                      Start-Etikett auf dem Bogen
                    </label>
                    <input type="number" className="form-control" min={1} max={189} value={labelForm.startPos}
                      onChange={(e) => setLabelForm({ ...labelForm, startPos: e.target.value })} />
                  </div>
                </div>
                <div className="form-check mb-2">
                  <input className="form-check-input" type="checkbox" id="lbl-double"
                    checked={Number(labelForm.perNumber) === 2}
                    onChange={(e) => setLabelForm({ ...labelForm, perNumber: e.target.checked ? 2 : 1 })} />
                  <label className="form-check-label" htmlFor="lbl-double">Jede Nummer doppelt drucken</label>
                </div>
                <div className="form-check mb-3">
                  <input className="form-check-input" type="checkbox" id="lbl-frames"
                    checked={labelForm.frames}
                    onChange={(e) => setLabelForm({ ...labelForm, frames: e.target.checked })} />
                  <label className="form-check-label" htmlFor="lbl-frames">
                    Etikettenrahmen mitdrucken <span className="text-muted">(nur für den Testdruck auf Normalpapier)</span>
                  </label>
                </div>
                <details className="mb-3">
                  <summary className="small text-muted" style={{ cursor: 'pointer' }}>Feinjustierung (wenn der Drucker versetzt)</summary>
                  <div className="row g-2 mt-1">
                    <div className="col-6">
                      <label className="form-label small">Versatz X (mm, + = rechts)</label>
                      <input type="number" step="0.5" className="form-control form-control-sm" value={labelForm.offX}
                        onChange={(e) => setLabelForm({ ...labelForm, offX: e.target.value })} />
                    </div>
                    <div className="col-6">
                      <label className="form-label small">Versatz Y (mm, + = unten)</label>
                      <input type="number" step="0.5" className="form-control form-control-sm" value={labelForm.offY}
                        onChange={(e) => setLabelForm({ ...labelForm, offY: e.target.value })} />
                    </div>
                  </div>
                </details>
                {labelStats() && (
                  <div className="alert alert-info py-2 small">
                    <strong>{labelStats().count} Etiketten</strong> auf <strong>{labelStats().pages} Bogen</strong>
                    {labelStats().restLast > 0 && <> – auf dem letzten Bogen bleiben {labelStats().restLast} Etiketten frei</>}.
                    <br />Drucken: <strong>Tatsächliche Größe / 100 %</strong>, keine Seitenanpassung, Bogen im Hochformat.
                  </div>
                )}
                <button className="btn btn-success w-100" onClick={generateLabelSheet} disabled={!labelStats()}>
                  🖨️ Etikettenbogen als PDF
                </button>
              </>
            )}

            {!['numbering', 'auto_numbering', 'hook_labels'].includes(activeSignType) && signTypes[activeSignType] && (
              <div className="alert alert-info">
                <strong>📋 {signTypes[activeSignType].name}</strong><br/>
                Dieses Schild wird automatisch mit vordefiniertem Text erstellt.
              </div>
            )}

            {activeSignType !== 'hook_labels' && (
            <button className="btn btn-primary" onClick={addSign} disabled={
              !signTypes[activeSignType] ||
              (activeSignType === 'numbering' && (!numberingForm.fromNumber || !numberingForm.toNumber)) ||
              (activeSignType === 'auto_numbering' && (!autoNumberingForm.startNumber || !autoNumberingForm.endNumber))
            }>
              ➕ {activeSignType === 'auto_numbering' ? 'Schilder automatisch erstellen' : 'Schild hinzufügen'}
            </button>
            )}
          </div>
        </div>
      </div>

      <div className="col-md-8">
        <div className="card">
          <div className="card-header d-flex justify-content-between align-items-center">
            <h5>📋 Schilder-Liste ({signs.length})</h5>
            {signs.length > 0 && (<button className="btn btn-success" onClick={generatePDF}>📄 PDF generieren</button>)}
          </div>
          <div className="card-body">
            {signs.length === 0 ? (
              <div className="text-center text-muted py-4">
                <div className="mb-3" style={{fontSize: '4rem'}}>🏷️</div>
                <p>Noch keine Schilder hinzugefügt.</p>
                <p>Wählen Sie links einen Schildtyp aus und fügen Sie ihn hinzu.</p>
              </div>
            ) : (
              <div className="list-group">
                {signs.map((sign, index) => (
                  <div key={sign.id} className="list-group-item">
                    <div className="d-flex justify-content-between align-items-start">
                      <div>
                        <h6 className="mb-1">{sign.type === 'numbering' ? '🔢' : (signTypes[sign.type]?.icon || '❓')} {signTypes[sign.type]?.name || sign.type}</h6>
                        {sign.type === 'numbering' ? (
                          <p className="mb-1"><strong>Nummern:</strong> {sign.fromNumber} - {sign.toNumber}<br/><strong>Pfeil:</strong> {arrowSymbols[sign.arrowDirection]}</p>
                        ) : (
                          <p className="mb-1"><strong>{sign.title}</strong><br/><small>{sign.content.substring(0, 100)}...</small></p>
                        )}
                      </div>
                      <button className="btn btn-sm btn-outline-danger" onClick={() => removeSign(sign.id)}>❌</button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};

export default SignageManager;